import { memo } from 'react';
import { FileText, Film, File, FileType2, Music } from 'lucide-react';
import { useAssetUrl } from '@common/services/asset-url-resolver';

interface AssetPreviewProps {
  type: string;
  url: string;
  /** Tile edge in px. Default 27 (the calendar chip); the Schedule-again dialog asks for a larger tile. */
  size?: number;
}

const DEFAULT_SIZE = 27;

const containerStyle = (size: number) => ({
  width: `${size}px`,
  height: `${size}px`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginInlineEnd: '3px',
  backgroundColor: '#e3e3e3',
  borderRadius: '4px',
  color: 'var(--bulk-text-secondary)',
});

const ImagePreview = memo(({ url, size }: { url: string; size: number }) => {
  const { resolvedUrl, isLoading, error } = useAssetUrl(url);

  if (isLoading) {
    return (
      <div style={{
        ...containerStyle(size),
        backgroundColor: '#f0f0f0',
      }}>
        <div style={{ fontSize: '10px', color: '#999' }}>...</div>
      </div>
    );
  }

  if (error || !resolvedUrl) {
    return (
      <div style={containerStyle(size)}>
        <File size={Math.round(size * 0.66)} />
      </div>
    );
  }

  return (
    <img
      alt={url}
      src={resolvedUrl}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        objectFit: 'cover',
        objectPosition: 'center',
        marginInlineEnd: '3px',
        borderRadius: '4px',
      }}
    />
  );
});
ImagePreview.displayName = 'ImagePreview';

const getImagePreview = (url: string, size: number) => <ImagePreview url={url} size={size} />;

const getVideoPreview = (size: number) => (
  <div style={containerStyle(size)}>
    <Film size={Math.round(size * 0.66)} />
  </div>
);

const getAudioPreview = (size: number) => (
  <div style={containerStyle(size)}>
    <Music size={Math.round(size * 0.66)} />
  </div>
);

const getApplicationPreview = (type: string, size: number) => {
  let Icon = File;

  if (type.includes('pdf')) {
    Icon = FileText;
  } else if (type.includes('word') || type.includes('doc') || type.includes('docx')) {
    Icon = FileType2;
  }

  return (
    <div style={containerStyle(size)}>
      <Icon size={Math.round(size * 0.66)} />
    </div>
  );
};

export const MessageAssetPreview = memo(({ type, url, size = DEFAULT_SIZE }: AssetPreviewProps) => {
  return (
    <>
      {type && url && type.includes('image') && getImagePreview(url, size)}
      {type && url && type.includes('video') && getVideoPreview(size)}
      {type && url && type.includes('audio') && getAudioPreview(size)}
      {type && url && type.includes('application') && getApplicationPreview(type, size)}
    </>
  );
}); 